import { GitBranchIcon, GitPullRequestArrowIcon } from 'lucide-react';

import type {
  ExecutionArtifactRefRole,
  TaskArtifactView,
} from '@/lib/api/generated/model';
import { runLineText } from '../artifactText';
import type { ForgeLinks } from '../forgeLinks';
import { ForgeLink } from './ForgeLink';

export interface RunArtifactLine {
  role: ExecutionArtifactRefRole;
  artifact: TaskArtifactView;
}

/**
 * What one run did outside the platform, in its own group (spec 119 FR-007,
 * FR-008): "Opened group/service!57", "Pushed to group/service!57", "Pushed
 * spike". Each links to the forge: a request by its own address, a branch by
 * the one `forgeLinks` builds.
 *
 * A run whose worker counted more than reached the record says so, in one
 * sentence — the list above it is what is known, not necessarily all there is.
 * Nothing at all for a run with neither (FR-010).
 */
export function RunArtifactLines({
  lines,
  incomplete,
  links,
}: {
  lines: readonly RunArtifactLine[];
  incomplete: boolean;
  /** Addresses for branches, which the record does not carry. */
  links?: ForgeLinks;
}) {
  if (lines.length === 0 && !incomplete) return null;
  return (
    <div className="flex flex-col gap-1">
      {lines.length > 0 ? (
        <ul
          aria-label="Created or updated"
          className="flex flex-col gap-1 text-sm"
        >
          {lines.map(({ role, artifact }) => {
            const text = runLineText(role, artifact);
            const Icon =
              artifact.kind === 'branch'
                ? GitBranchIcon
                : GitPullRequestArrowIcon;
            return (
              <li
                key={`${role}:${artifact.id}`}
                className="flex min-w-0 flex-wrap items-baseline gap-x-2"
              >
                <Icon
                  aria-hidden="true"
                  className="text-muted-foreground size-3.5 shrink-0 self-center"
                />
                <ForgeLink
                  href={
                    artifact.kind === 'branch'
                      ? (links?.branch(artifact, artifact.source_branch) ??
                        null)
                      : artifact.web_url
                  }
                  className="[overflow-wrap:anywhere]"
                >
                  {text}
                </ForgeLink>
              </li>
            );
          })}
        </ul>
      ) : null}
      {incomplete ? (
        <p className="text-muted-foreground text-xs">
          Some things this run created may be missing from this list.
        </p>
      ) : null}
    </div>
  );
}
